import React from "react"
import autobind from "class-autobind"
import Table from "../../../components/common/Table"
import { CircularProgress, TableHeader, TableRow, TableHeaderColumn, TableBody, TableRowColumn } from "material-ui"
import strings from "../../../localization/strings"
import * as api from "../../../api"
import { IDate, IWorkingEmployee, IWorkingEmployeeSearch } from "../../../models"
import AccountStore from "../../../stores/AccountStore"
import ImageLoader from "../../../components/misc/ImageLoader"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import * as timeHelper from "../../../helpers/timeHelper"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import { browserHistory } from "react-router"
import * as userHelper from "../../../helpers/userHelper"
import ExternalLink from "../../../components/common/externalLink/ExternalLink"
import ClientLocalDateTimeStr from "../../../components/common/ClientLocalDateTimeStr"
import DashboardWorkingEmployeeReports from "./DashboardWorkingEmployeeReports"

interface IProps {
    utcTime: IDate
    globalHierarchyItemId: string
    filterByPunchPermissionHierarchy: boolean
    filterDirectReportsOnly: boolean
}
interface IState {
    loading: boolean
    offset: number
    limit: number
    total: number
    workingEmployees: IWorkingEmployee[]
}

export default class DashboardWorkingEmployeeDetail extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: true,
            offset: 0,
            limit: 25,
            total: 0,
            workingEmployees: []
        }
        autobind(this)
    }

    componentDidMount() {
        this.getData(this.props)
    }

    componentWillReceiveProps(nextProps: IProps) {
        (
            this.props.globalHierarchyItemId !== nextProps.globalHierarchyItemId ||
            this.props.utcTime !== nextProps.utcTime ||
            this.props.filterByPunchPermissionHierarchy !== nextProps.filterByPunchPermissionHierarchy ||
            this.props.filterDirectReportsOnly !== nextProps.filterDirectReportsOnly
        ) &&
            this.setState({
                workingEmployees: [],
                offset: 0
            }, () =>
                this.getData(nextProps)
            )
    }

    getData(props: IProps) {
        if (!userHelper.getSupervisorAccessPermissionForHierarchyId(props.globalHierarchyItemId))
            return

        this.setState({ loading: true }, () =>
            api.getDashboardWorkingEmployees(props.globalHierarchyItemId, props.utcTime, props.filterDirectReportsOnly, props.filterByPunchPermissionHierarchy, this.state.offset, this.state.limit).then(res => {
                const result = res.data as IWorkingEmployeeSearch
                this.setState({
                    loading: false,
                    total: result.TotalCount,
                    workingEmployees: result.Employees
                })
            })
        )
    }

    onClickEmployeeCol(userId) {
        permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.ViewEmployees) &&
            permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards) &&
            browserHistory.push("/user-details/" + userId + "/timecards")
    }

    render() {
        const hierarchyColLabel = this.props.filterByPunchPermissionHierarchy ? strings.Punch_Permission_Hierarchy : strings.Default_Hierarchy
        return (
            <div style={{ padding: 15 }}>
                <div style={{ margin: 10, display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 20, fontWeight: 500 }}>
                        {strings.Working_Employees}
                    </div>
                    <DashboardWorkingEmployeeReports
                        utcTime={this.props.utcTime}
                        globalHierarchyItemId={this.props.globalHierarchyItemId}
                        filterByPunchPermissionHierarchy={this.props.filterByPunchPermissionHierarchy}
                        filterDirectReportsOnly={this.props.filterDirectReportsOnly}
                    />
                </div>
                {this.state.loading ?
                    <div className="spinner">
                        <CircularProgress />
                    </div> :
                    this.state.workingEmployees.length === 0 ?
                        <div>
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>
                                {strings.No_working_employee_found}
                            </h2>
                        </div> :
                        <Table
                            readonly
                            items={this.state.workingEmployees}
                            total={this.state.total}
                            offset={this.state.offset}
                            limit={this.state.limit}
                            limitOptions={[25, 50, 100]}
                            onPageClick={offset => this.setState({ offset }, () => this.getData(this.props))}
                            onLimitChange={limit => this.setState({ limit, offset: 0 }, () => this.getData(this.props))}
                        >
                            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                <TableRow>
                                    <TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 15 }} />
                                    <TableHeaderColumn style={styles.imageColumnHeader} />
                                    <TableHeaderColumn className="TextOverflow" title={strings.Name} style={styles.columnHeader}>{strings.Name}</TableHeaderColumn>
                                    <TableHeaderColumn className="TextOverflow" title={hierarchyColLabel} style={{ ...styles.columnHeader, width: 150 }}>
                                        {hierarchyColLabel}
                                    </TableHeaderColumn>
                                    <TableHeaderColumn className="TextOverflow" title={strings.Punch_Hierarchy} style={styles.columnHeader}>{strings.Punch_Hierarchy}</TableHeaderColumn>
                                    {AccountStore.getJobsEnabled() &&
                                        <TableHeaderColumn className="TextOverflow" title={strings.Job} style={styles.columnHeader}>{strings.Job}</TableHeaderColumn>
                                    }
                                    {AccountStore.getPhasesEnabled() &&
                                        <TableHeaderColumn className="TextOverflow" title={strings.Phase} style={styles.columnHeader}>{strings.Phase}</TableHeaderColumn>
                                    }
                                    <TableHeaderColumn className="TextOverflow" title={strings.Position} style={styles.columnHeader}>{strings.Position}</TableHeaderColumn>
                                    {AccountStore.getPayClassEnabled() &&
                                        <TableHeaderColumn className="TextOverflow" title={strings.Pay_Class} style={styles.columnHeader}>{strings.Pay_Class}</TableHeaderColumn>
                                    }
                                    <TableHeaderColumn className="TextOverflow" title={strings.In} style={styles.timeColumnHeader}>{strings.In}</TableHeaderColumn>
                                    {AccountStore.getScheduleEnabled() &&
                                        <TableHeaderColumn className="TextOverflow" title={strings.Scheduled_In} style={styles.timeColumnHeader}>{strings.Scheduled_In}</TableHeaderColumn>
                                    }
                                    {AccountStore.getScheduleEnabled() &&
                                        <TableHeaderColumn className="TextOverflow" title={strings.Scheduled_Out} style={styles.timeColumnHeader}>{strings.Scheduled_Out}</TableHeaderColumn>
                                    }
                                    <TableHeaderColumn className="TextOverflow" title={strings.Current_Duration} style={styles.timeColumnHeader}>{strings.Current_Duration}</TableHeaderColumn>
                                </TableRow>
                            </TableHeader>
                            <TableBody displayRowCheckbox={false} deselectOnClickaway={false}>
                                {this.state.workingEmployees.map((row, i) =>
                                    <TableRow key={i}>
                                        <TableRowColumn style={{ width: 25, paddingLeft: 5, paddingRight: 15, paddingTop: 4 }}>
                                            <ExternalLink
                                                url={"/user-details/" + row.Id + "/" + userHelper.getValidSubPages()[0]}
                                            />
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.imageColumn}>
                                            {(row.ProfileImageID || row.ProfileImageS3Url) &&
                                                <div onClick={() => this.onClickEmployeeCol(row.Id)}>
                                                    <ImageLoader url={row.ProfileImageS3Url} fixedHeight={75} avatar={true} src={"/users/profileimage/" + row.ProfileImageID} key={`smallImg-${i}`} />
                                                </div>
                                            }
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.columnValue}>
                                            <div onClick={() => this.onClickEmployeeCol(row.Id)} style={{ fontWeight: "bold" }}>
                                                {row.UserName}
                                                <br></br>
                                                <span style={{ color: "rgb(158, 158, 158)", fontSize: 10 }}>Emp #: {row.UserExternalId}</span>
                                            </div>
                                        </TableRowColumn>
                                        <TableRowColumn style={{ ...styles.columnHeader, width: 150 }} title={hierarchyHelper.getFullPathLabel(this.props.filterByPunchPermissionHierarchy ? row.UserDefaultHierarchyItemId : row.UserDefaultPunchHierarchyId)}>
                                            {hierarchyHelper.getFullyQualifiedLevelNameById(this.props.filterByPunchPermissionHierarchy ? row.UserDefaultHierarchyItemId : row.UserDefaultPunchHierarchyId)}
                                        </TableRowColumn>
                                        <TableRowColumn style={styles.columnValue} title={hierarchyHelper.getFullPathLabel(row.HierarchyId)}>
                                            {hierarchyHelper.getFullyQualifiedLevelNameById(row.HierarchyId)}
                                        </TableRowColumn>
                                        {AccountStore.getJobsEnabled() &&
                                            <TableRowColumn style={styles.columnValue} title={row.JobName}>{row.JobName}</TableRowColumn>
                                        }
                                        {AccountStore.getPhasesEnabled() &&
                                            <TableRowColumn style={styles.columnValue} title={row.PhaseName}>{row.PhaseName}</TableRowColumn>
                                        }
                                        <TableRowColumn style={styles.columnValue}>{row.PositionName}</TableRowColumn>
                                        {AccountStore.getPayClassEnabled() &&
                                            <TableRowColumn style={styles.columnValue} title={row.PayClassName}>{row.PayClassName}</TableRowColumn>
                                        }
                                        <TableRowColumn style={styles.timeColumnValue}>
                                            <ClientLocalDateTimeStr
                                                hierarchyId={row.HierarchyId}
                                                jobId={row.JobId}
                                                utcTime={row.PunchInTime}
                                                formatStr="h:mma"
                                            />
                                        </TableRowColumn>
                                        {AccountStore.getScheduleEnabled() &&
                                            <TableRowColumn style={styles.timeColumnValue}>
                                                {row.SchedInTime && timeHelper.convertDefaultDateToNull(row.SchedInTime as string) &&
                                                    <ClientLocalDateTimeStr
                                                        hierarchyId={row.HierarchyId}
                                                        jobId={row.JobId}
                                                        utcTime={row.SchedInTime}
                                                        formatStr="h:mma"
                                                    />
                                                }
                                            </TableRowColumn>
                                        }
                                        {AccountStore.getScheduleEnabled() &&
                                            <TableRowColumn style={styles.timeColumnValue}>
                                                {row.SchedOutTime && timeHelper.convertDefaultDateToNull(row.SchedOutTime as string) &&
                                                    <ClientLocalDateTimeStr
                                                        hierarchyId={row.HierarchyId}
                                                        jobId={row.JobId}
                                                        utcTime={row.SchedOutTime}
                                                        formatStr="h:mma"
                                                    />
                                                }
                                            </TableRowColumn>
                                        }
                                        <TableRowColumn style={styles.timeColumnValue}>{row.ActiveHours}</TableRowColumn>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                }
            </div>
        )
    }
}

const styles = {
    columnValue: {
        textAlign: "left",
        cursor: "pointer",
        paddingLeft: 5,
        paddingRight: 5
    },
    columnHeader: {
        textAlign: "left",
        paddingLeft: 5,
        paddingRight: 5
    },
    timeColumnValue: {
        textAlign: "center",
        cursor: "pointer",
        paddingLeft: 5,
        paddingRight: 5,
        width: 90
    },
    timeColumnHeader: {
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
        width: 90
    },
    imageColumnHeader: {
        width: 40,
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
    },
    imageColumn: {
        width: 40,
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
        cursor: "pointer"
    }
}